import { useEffect, useRef, useState, type RefObject } from 'react'
import type { LifeSpace } from '../../../lib/db/models'
import { onAppEvent } from '../../../lib/events'
import { consumePendingPageJump, putBackPageJump, type TaskJumpPage } from '../../../lib/taskJump/pendingTaskJump'
import { whenTaskRowSettled } from '../../../lib/taskJump/whenTaskRowSettled'

/**
 * Holt einen gemerkten Sprung auf diese Seite ab (#210, Klick in „Favoriten“): Erst wenn die Seite
 * fertig geladen ist (`whenTaskRowSettled`), wird die Aufgabe ausgewählt und ins Bild gerollt.
 *
 * Für Seiten ohne Aufgaben-Details (Ruhend, Wiederkehrend, Geschafft); Kurz notiert und Demnächst
 * öffnen zusätzlich die Details und holen selbst ab.
 */
export function usePendingPageJump({ page, lifeSpace, isActive, ready, rootRef, select }: {
  page: TaskJumpPage
  lifeSpace: LifeSpace
  isActive: boolean
  /** Die Aufgaben der Seite sind geladen. */
  ready: boolean
  /** Gesucht wird nur hier — derselbe Eintrag kann in einem anderen, verborgenen Tab stehen. */
  rootRef: RefObject<HTMLElement | null>
  /** Wählt die Aufgabe aus; ihre Zeile steht dann schon da. */
  select: (taskId: string) => void
}): void {
  const selectRef = useRef(select)
  selectRef.current = select
  // Steht die Seite schon offen, liefe der Effekt sonst nicht noch einmal.
  const [signal, setSignal] = useState(0)
  useEffect(() => onAppEvent('pending-task-jump', () => setSignal(n => n + 1)), [])

  useEffect(() => {
    if (!isActive || !ready) return
    const pending = consumePendingPageJump(page, lifeSpace)
    if (!pending) return
    let done = false
    const cancel = whenTaskRowSettled({
      root: () => rootRef.current,
      taskId: pending.taskId,
      run: row => {
        done = true
        selectRef.current(pending.taskId)
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
    })
    return () => {
      cancel()
      // Abgebaut, bevor es lief (StrictMode beim Einhängen): Der nächste Lauf soll ihn finden.
      if (!done) putBackPageJump(page, lifeSpace, pending)
    }
  }, [page, lifeSpace, isActive, ready, rootRef, signal])
}
